import './styles.scss';

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';

import { isBrowser } from 'Helpers/Browser';
import { getSlug } from 'Helpers/Uri';
import { getLastPage } from 'Helpers/Pagination';
import { forceHttpsUri } from 'Helpers/Uri';
import { getFullSlug } from 'Helpers/Photos';
import { isPhotoInLocalStorageCart } from 'Helpers/Cart';
import { getRouteByName } from 'Helpers/Routes';

import Head from 'Components/helpers/Head';
import Gallery from 'Components/layout/Gallery';
import GalleryNoResult from 'Components/layout/GalleryNoResult';
import Pagination from 'Components/layout/Pagination';
import Spinner from 'Components/layout/Spinner';
import AlbumDetails from 'Components/public/album/Details';
import AlbumHeader from 'Components/public/album/Header';
import NotFoundContainer from 'Containers/global/NotFound';

import { fetch, updatePhotos } from 'Redux/actions/albums';
import { addItem as cartAddItem } from 'Redux/actions/cart';
import { addItem as lightboxAddItem, removeItem as lightboxRemoveItem } from 'Redux/actions/lightbox';

@connect(
    (state) => ({
        albums: state.albums,
        user: state.user,
        photos: state.photos,
        cart: state.cart,
        lightbox: state.lightbox
    }), { cartAddItem, lightboxAddItem, lightboxRemoveItem, fetch, updatePhotos }
)
export default class Album extends React.Component {
    static propTypes = {
        fetch: PropTypes.func.isRequired,
        updatePhotos: PropTypes.func.isRequired,
        cartAddItem: PropTypes.func.isRequired,
        lightboxAddItem: PropTypes.func.isRequired,
        lightboxRemoveItem: PropTypes.func.isRequired,
        albums: PropTypes.shape({
            albums: PropTypes.objectOf(
                PropTypes.shape({
                    isPending: PropTypes.bool.isRequired,
                    isLoaded: PropTypes.bool.isRequired,
                    album: PropTypes.shape({
                        id: PropTypes.string.isRequired,
                        name: PropTypes.string.isRequired,
                        description: PropTypes.string,
                        coverPhoto: PropTypes.shape({
                            w1280: PropTypes.string.isRequired
                        }),
                        user: PropTypes.shape({
                            username: PropTypes.string.isRequired,
                            avatar: PropTypes.shape({
                                w120: PropTypes.string.isRequired
                            }).isRequired
                        }).isRequired,
                        photos: PropTypes.shape({
                            page: PropTypes.number.isRequired,
                            total: PropTypes.number.isRequired,
                            elements: PropTypes.arrayOf(
                                PropTypes.shape({
                                    id: PropTypes.string.isRequired,
                                    slug: PropTypes.string,
                                    width: PropTypes.number.isRequired,
                                    height: PropTypes.number.isRequired,
                                    selectedCaption: PropTypes.string,
                                    resolutions: PropTypes.object.isRequired
                                })
                            ).isRequired
                        })
                    })
                })
            ).isRequired
        }).isRequired,
        cart: PropTypes.shape({
            items: PropTypes.array
        }).isRequired,
        lightbox: PropTypes.shape({
            items: PropTypes.array,
        }).isRequired,
        user: PropTypes.shape({
            accessToken: PropTypes.string,
            currentBrandId: PropTypes.string,
            currentBusinessId: PropTypes.string,
            profile: PropTypes.shape({
                admin: PropTypes.bool.isRequired,
                lightbox: PropTypes.shape({
                    photosCount: PropTypes.number,
                    photos: PropTypes.arrayOf(
                        PropTypes.shape({
                            id: PropTypes.string
                        })
                    )
                })
            })
        }),
        params: PropTypes.shape({
            username: PropTypes.string,
            slug: PropTypes.string,
            page: PropTypes.string
        })
    };
    static defaultProps = {
        params: {
            id: '',
            page: ''
        }
    };

    constructor(props) {
        super(props);
        const searchParams = this.getSearchParams(props);

        this.state = {
            perPage: 40,
            pageNumber: searchParams.pageNumber ? searchParams.pageNumber : 1,
            albumWrapper: props.albums.albums.hasOwnProperty(searchParams.albumId) ? props.albums.albums[searchParams.albumId] : null
        };
    }

    getSearchParams = (props) => {
        let { username, slug, page } = props.params;
        let pageString = typeof page !== 'undefined' && page ? page : 'page-1';
        let pageStringNumber = pageString ? pageString.replace('page-', '') : '';
        let pageNumber = pageStringNumber && !isNaN(pageStringNumber) ? parseInt(pageStringNumber) : 1;
        let slugParams = getSlug(props.params);

        return {
            username: username,
            albumId: slugParams.id,
            albumSlug: slugParams.name,
            pageString: pageString,
            pageNumber: pageNumber
        };
    }

    componentDidMount = () => {
        const { albums } = this.props.albums;
        const { albumId, pageNumber } = this.getSearchParams(this.props);

        if (!albums.hasOwnProperty(albumId)) {
            this.fetchPhotos(albumId, pageNumber);
        }
    }

    componentWillReceiveProps = props => {
        const { albums } = props.albums;
        const { albumId, pageNumber } = this.getSearchParams(props);

        if (isBrowser() && (
            (this.props.user.currentBusinessId !== props.user.currentBusinessId) ||
            (pageNumber !== this.state.pageNumber)
        )) {
            this.fetchPhotos(albumId, pageNumber);
        }

        if (albums.hasOwnProperty(albumId) && albums[albumId]) {
            this.setState({
                ...this.state,
                pageNumber: pageNumber,
                albumWrapper: albums[albumId]
            });
        }
    }

    fetchPhotos = (albumId, page) => {
        this.props.fetch({
            id: albumId,
            page: page,
            perPage: this.state.perPage
        });
    }

    onAddToCart = photo => {
        const { albumId } = this.getSearchParams(this.props);
        const photoWithAlbum = Object.assign({}, photo, { albumId: albumId });
        this.props.cartAddItem(photoWithAlbum, 'album');
    }
    onAddToLightbox = photo => {
        const { albumId } = this.getSearchParams(this.props);
        const photoWithAlbum = Object.assign({}, photo, { albumId: albumId });
        this.props.lightboxAddItem(photoWithAlbum, 'album');
    }

    render() {
        const { albumWrapper, perPage } = this.state;
        const searchParams = this.getSearchParams(this.props);
        const albumId = searchParams.albumId;

        //If data is not loaded yet
        if (!albumWrapper || !albumWrapper.isLoaded || albumWrapper.isPending) {
            return (
                <div className="container-album">
                    <Spinner />
                </div>
            );
        }

        //If data is loaded but there is no result
        if (!albumWrapper.album) {
            return (<NotFoundContainer params={this.props.params} />);
        }

        //Data is loaded successfully
        const { album } = albumWrapper;
        let lastPage = getLastPage(album.photos.total, perPage);

        return (
            <div className="container-album scroll-section-parent">
                <Head
                    title={`${album.name} stock photos by ${album.user.username} – Foap.com`}
                    description={`${album.name} - ${album.photos.total} stock photos. Album by ${album.user.username}. Download images and pictures from Foap's community of photographers.`}
                    canonical={getRouteByName('album', { username: album.user.username, slug: this.props.params.slug, page: '' }).path}
                    robots={'noindex, nofollow'}
                    image={forceHttpsUri(album.coverPhoto.w1280)}
                    relPrev={searchParams.pageNumber > 1 ? getRouteByName('album', { username: album.user.username, slug: this.props.params.slug, page: searchParams.pageString.replace(searchParams.pageNumber, (searchParams.pageNumber - 1)) }).path : ''}
                    relNext={searchParams.pageNumber < lastPage ? getRouteByName('album', { username: album.user.username, slug: this.props.params.slug, page: searchParams.pageString.replace(searchParams.pageNumber, (searchParams.pageNumber + 1)) }).path : ''}
                />
                <AlbumHeader album={album} />
                <div className="album-body scroll-section-static">
                    <div className="container-fluid">
                        <AlbumDetails album={album} />
                        <Gallery
                            isLoading={albumWrapper.isPending}
                            noResultInfo={<GalleryNoResult page={searchParams.pageNumber} />}
                            overlayType={'default'}
                            photos={album.photos.elements.map(photo => ({
                                ...photo,
                                href: getRouteByName('singlePhoto', { slug: getFullSlug(photo), albumId: album.id }).path,
                                controls: {
                                    curate: {
                                        visible: this.props.user.profile && this.props.user.profile.admin
                                    },
                                    addToCart: {
                                        disabled: photo.isInCart || isPhotoInLocalStorageCart(photo),
                                        visible: !photo.downloadUrl && (!this.props.user.accessToken || photo.purchasable),
                                        trigger: photo => this.props.cartAddItem(photo, 'user')
                                            .then(this.props.updatePhotos({ id: albumId, photos: [{ id: photo.id, payload: { isInCart: true } }] }))
                                    },
                                    addToLightbox: {
                                        disabled: photo.isInLightbox,
                                        visible: !!this.props.user.accessToken && !photo.isInLightbox,
                                        trigger: photo => this.props.lightboxAddItem(photo, 'user')
                                            .then(this.props.updatePhotos({ id: albumId, photos: [{ id: photo.id, payload: { isInLightbox: true } }] }))
                                    },
                                    removeFromLightbox: {
                                        disabled: !photo.isInLightbox,
                                        visible: !!this.props.user.accessToken && photo.isInLightbox,
                                        trigger: photo => this.props.lightboxRemoveItem(photo, 'user')
                                            .then(this.props.updatePhotos({ id: albumId, photos: [{ id: photo.id, payload: { isInLightbox: false } }] }))
                                    },
                                    download: {
                                        disabled: false,
                                        visible: photo.downloadUrl
                                    }
                                }
                            }))}
                        />
                        <Pagination
                            totalElements={parseInt(album.photos.total)}
                            elementsPerPage={parseInt(perPage)}
                            currentPage={parseInt(searchParams.pageNumber)}
                            link={getRouteByName('album', { username: album.user.username, slug: this.props.params.slug, page: 'page-%pageNumber%' }).path}
                        />
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/containers/global/Album/index.js